/**
 * The topbar's search field.
 *
 * Was an icon and the word "Search" that navigated to /browse and left you to
 * find the search box there. The field is the same journey with the typing
 * moved to the front: submitting seeds the browse filter the page already
 * reads (`setFilterQuery`) and then goes there, so nothing new is fetched and
 * /browse renders the results on arrival.
 *
 * A `Box` pill around a bare `InputBase`, not a `TextField`: the theme pins
 * every outlined field to the 8px control radius, and the design's search is a
 * pill. Wrapping an unstyled base is how this gets the oval without reaching
 * into the theme to special-case one field.
 */
import React, { useState } from "react";
import { Box, InputBase } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { setFilterQuery } from "@/store/projectProfile-v1/projectProfileActions";
import { setHandleRedirect } from "@/store/sideNav/sideNavActions";
import radii from "@/theme/radii";
import tokens from "@/theme/tokens";

export default function HeaderSearch() {
  const { t } = useTranslation(["translation.navigation"]);
  const dispatch = useDispatch();
  const [term, setTerm] = useState("");

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    dispatch(setFilterQuery(term.trim() || null));
    dispatch(setHandleRedirect(null, "/browse", undefined));
  };

  return (
    <Box
      component="form"
      role="search"
      onSubmit={submit}
      sx={{
        flex: 1,
        maxWidth: 340,
        ml: 1.5,
        display: { xs: "none", sm: "flex" },
        alignItems: "center",
        gap: 1,
        height: 40,
        px: 2,
        border: "1px solid",
        borderColor: tokens.color.inputBorder,
        borderRadius: `${radii.submitButton}px`,
        bgcolor: "common.white",
        "&:focus-within": { borderColor: "primary.main" },
      }}
    >
      <SearchIcon fontSize="small" sx={{ color: "text.disabled" }} />
      <InputBase
        value={term}
        onChange={(event) => setTerm(event.target.value)}
        placeholder={String(t("Search"))}
        inputProps={{ "aria-label": String(t("Search")) }}
        sx={{ flex: 1, minWidth: 0, "& input": { p: 0 } }}
      />
    </Box>
  );
}
